import React from 'react';
import { Check, Loader2 } from 'lucide-react';
import { getAllModules } from '../../config/moduleConfig';

/**
 * Map API backend codes → frontend module visual config (icon, color, etc.)
 * This is the ONLY place we reference moduleConfig — purely for visuals.
 */
const CODE_TO_MODULE_VISUAL = (() => {
    const codeMapping = {
        'POS': 'pos',
        'KDS': 'kds',
        'INVENTORY': 'inventory',
        'REPORTS': 'finance',
        'BACKOFFICE': 'backoffice',
        'CRM': 'crm',
        'HRM': 'hrm',
        'AI_INSIGHTS': 'ai-insights',
        'INTEGRATIONS': 'integrations',
        'CAPTAIN_APP': 'captain-app',
        'QR_MENU': 'qr-menu',
    };

    const allModules = getAllModules();
    const visualMap = {};

    Object.entries(codeMapping).forEach(([backendCode, frontendId]) => {
        const mod = allModules.find(m => m.id === frontendId);
        if (mod) {
            visualMap[backendCode] = {
                icon: mod.icon,
                color: mod.color, 
                // description: mod.description,
                // features: mod.features,
            };
        }
    });

    return visualMap;
})();

const badgeColorClasses = {
    indigo: 'bg-[#FFE8C8] text-[#C45500]',
    indigo: 'bg-[#FFE8C8] text-[#C45500]',
    amber: 'bg-amber-100 text-amber-700',
    emerald: 'bg-emerald-100 text-emerald-700',
    blue: 'bg-[#FFE8C8] text-[#C45500]',
    rose: 'bg-rose-100 text-rose-700',
    violet: 'bg-violet-100 text-violet-700',
    orange: 'bg-orange-100 text-orange-700',
    cyan: 'bg-cyan-100 text-cyan-700',
    sky: 'bg-sky-100 text-sky-700',
    purple: 'bg-[#F0F0F0] text-[#37475A]',
};

/**
 * SubscriptionTab
 *
 * Props:
 *  - apiModules: Array<{ code, name, active, subscription }>  — from API
 *  - handleToggleModule: (moduleCode: string) => void
 *  - loading: boolean
 */
export default function SubscriptionTab({
    apiModules = [],
    handleToggleModule,
    loading,
}) {
    const subscriptionCount = apiModules.filter(m => m.subscription).length;

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="animate-spin text-[#C45500]" size={32} />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Summary Header */}
            <div className="bg-white border border-slate-200 rounded-xl p-5 flex items-center justify-between">
                <div>
                    <h2 className="text-lg font-bold text-slate-900">Module Subscriptions</h2>
                    <p className="text-sm text-slate-500">
                        Click on module cards to enable/disable subscriptions
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="text-right">
                        <div className="text-xl font-extrabold text-slate-900">
                            {subscriptionCount}
                            <span className="text-slate-400">/{apiModules.length}</span>
                        </div>
                        <div className="text-xs text-slate-500">Active</div>
                    </div>
                    <div className="w-10 h-10 rounded-lg bg-[#FFE8C8] text-[#C45500] font-bold flex items-center justify-center">
                        ✓
                    </div>
                </div>
            </div>

            {/* Module Cards Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                {apiModules.map(module => {
                    const isSubscribed = module.subscription === true;
                    const visual = CODE_TO_MODULE_VISUAL[module.code];
                    const ModuleIcon = visual?.icon || null;
                    const badgeClass = badgeColorClasses[visual?.color] || badgeColorClasses.indigo;

                    return (
                        <button
                            key={module.code}
                            onClick={() => handleToggleModule(module.code)}
                            className={`relative bg-white rounded-xl shadow-lg overflow-hidden transition-all transform hover:scale-105 hover:shadow-xl text-left ${
                                isSubscribed
                                    ? 'ring-2 ring-[#f08804] ring-offset-2 opacity-100'
                                    : 'opacity-50 hover:opacity-70'
                            }`}
                        >
                            {/* Card Header */}
                            <div className="p-4 flex items-center gap-3 bg-white border-b border-slate-100">
                                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${badgeClass} ${!isSubscribed ? 'opacity-75' : ''}`}>
                                    {ModuleIcon
                                        ? <ModuleIcon size={22} />
                                        : <span className="text-xs font-bold">
                                            {module.name.split(' ').map(w => w[0]).slice(0, 2).join('')}
                                          </span>
                                    }
                                </div>

                                <div className="flex-1 min-w-0">
                                    <h4 className={`text-sm font-bold truncate ${isSubscribed ? 'text-slate-900' : 'text-slate-600'}`}>
                                        {module.name}
                                    </h4>
                                    {visual?.description && (
                                        <p className={`text-xs mt-0.5 truncate ${isSubscribed ? 'text-slate-500' : 'text-slate-400'}`}>
                                            {visual.description}
                                        </p>
                                    )}
                                </div>

                                {isSubscribed && (
                                    <div className="w-6 h-6 bg-[#232F3E] rounded-full flex items-center justify-center flex-shrink-0">
                                        <Check size={14} className="text-white" strokeWidth={3} />
                                    </div>
                                )}
                            </div>

                            {/* Card Body */}
                            <div className={`p-4 space-y-3 ${isSubscribed ? 'bg-slate-50' : 'bg-slate-100'}`}>
                                <div className="flex flex-wrap gap-2">
                                    {visual?.features?.slice(0, 3).map((feature, idx) => (
                                        <span
                                            key={idx}
                                            className={`text-xs px-2 py-1 rounded-full font-medium ${badgeClass} ${!isSubscribed ? 'opacity-70' : ''}`}
                                        >
                                            {feature}
                                        </span>
                                    ))}
                                </div>

                                <div className="flex items-center justify-between pt-1">
                                    <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                                        isSubscribed ? 'bg-[#FFE8C8] text-[#C45500]' : 'bg-slate-200 text-slate-500'
                                    }`}>
                                        {isSubscribed ? 'Subscribed' : 'Not Subscribed'}
                                    </span>
                                    <span className={`text-xs ${module.active ? 'text-emerald-600' : 'text-rose-500'}`}>
                                        {module.active ? '● Active' : '● Inactive'}
                                    </span>
                                </div>
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}